<template>
  <Transition name="load-error-banner">
    <div v-if="show" class="load-error-banner" role="alert" aria-live="assertive">
      <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
      <span>{{ t('errors.data_load_failed') }}</span>
      <button type="button" class="load-error-banner__retry" :disabled="isFirestoreLoading" @click="retry">
        {{ t('errors.retry') }}
      </button>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue"
import { useI18n } from "vue-i18n"
import { currentUser, isAuthenticated } from "../stores/authStore"
import {
  firestoreError,
  initializeFirestore,
  isFirestoreInitialized,
  isFirestoreLoading,
} from "../stores/firestoreStore"

// A hard initial-load failure used to be invisible: views gate their skeletons
// on isLoading && !isInitialized, so a failed init (isLoading false,
// isInitialized false) rendered every tab's EMPTY state — which reads as
// "all my data is gone". This banner names the real problem and offers retry.
// The offline case is deliberately excluded: OfflineBanner owns that story,
// and a retry while disconnected can only fail again.

const { t } = useI18n()

const isOffline = ref(typeof navigator !== "undefined" && !navigator.onLine)
function onOnline() {
  isOffline.value = false
}
function onOffline() {
  isOffline.value = true
}
onMounted(() => {
  window.addEventListener("online", onOnline)
  window.addEventListener("offline", onOffline)
})
onUnmounted(() => {
  window.removeEventListener("online", onOnline)
  window.removeEventListener("offline", onOffline)
})

const show = computed(
  () =>
    isAuthenticated.value &&
    !isOffline.value &&
    !!firestoreError.value &&
    !isFirestoreInitialized.value &&
    !isFirestoreLoading.value,
)

async function retry() {
  if (!currentUser.value) return
  await initializeFirestore(currentUser.value)
}
</script>

<style scoped>
.load-error-banner {
  position: fixed;
  top: calc(var(--safe-top) + 0.5rem);
  left: 50%;
  transform: translateX(-50%);
  z-index: var(--z-banner);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  max-width: calc(100vw - 2rem);
  padding: 0.5rem 0.875rem;
  border-radius: 999px;
  background: var(--ruby-600, #b83b3b);
  color: var(--ink-50, #faf9f7);
  font-size: 0.8125rem;
  box-shadow: var(--shadow-lg);
}

.load-error-banner__retry {
  background: none;
  border: 1px solid currentColor;
  border-radius: 999px;
  color: inherit;
  font: inherit;
  font-weight: 600;
  padding: 0.125rem 0.625rem;
  cursor: pointer;
  flex-shrink: 0;
}

.load-error-banner__retry:disabled {
  opacity: 0.6;
  cursor: default;
}

.load-error-banner-enter-active,
.load-error-banner-leave-active {
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;
}

.load-error-banner-enter-from,
.load-error-banner-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-8px);
}
</style>
